import {
  QLookup,
  QModalHeader,
  QResourceLabel,
  QTag,
  QText,
  SearchResource,
} from '@qualio/ui-components';
import { useCallback, useMemo } from 'react';

import { ResourceData } from '@qualio/search-qri';
import { QuickSearchQueryClause } from '@qualio/ui-components/lib/QOrganisms/Table/DataProviders';
import { globalSearchServiceApiClient } from '../../../../api/globalSearchService';
import { DocumentStatus } from '../../../../api/model/document';
import { qriApiClient } from '../../../../api/qri';
import { formatDate } from '../../../../util/DateUtils';
import { DocumentStatusIdToStatusConfigMap } from '../../../../util/WorkspaceUtils';

// QuickSearchV2 rows carry a top-level `id` at runtime that ResourceData
// doesn't declare; add it so the lookup can key/select rows on it.
type ReusableContentRow = ResourceData & { id: string };

type ReusableContentModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (resource: ResourceData) => void;
};

const LOOKUP_RESOURCES: SearchResource[] = ['document'];
const SEARCH_CLAUSE: QuickSearchQueryClause[] = [
  { term: { 'status.keyword': 'effective' } },
  { term: { documentFormat: 'qualio_document' } },
];

export const ReusableContentModal = ({
  isOpen,
  onClose,
  onSelect,
}: ReusableContentModalProps) => {
  const view: QLookup.DataView<ReusableContentRow> = useMemo(
    () => ({
      name: {
        header: 'Title',
        width: '55%',
        render: (displayLabel: string, row: ReusableContentRow) => (
          <QResourceLabel
            domain={row.resource as SearchResource}
            link={row.urls.view ?? row.identifiers.qri}
            openInNewTab
          >
            {displayLabel}
          </QResourceLabel>
        ),
      },
      date: {
        header: 'Last modified',
        width: '25%',
        render: (date: ReusableContentRow['date']) => (
          <QText>
            {date.lastModified ? formatDate(new Date(date.lastModified)) : '-'}
          </QText>
        ),
      },
      status: {
        header: 'Status',
        width: '20%',
        render: (status: ReusableContentRow['status']) =>
          status ? (
            <QTag variantColor="default">
              {DocumentStatusIdToStatusConfigMap[status as DocumentStatus]
                ?.label ?? status}
            </QTag>
          ) : null,
      },
    }),
    [],
  );

  const handleSelect = useCallback(
    async (selection: readonly ReusableContentRow[]) => {
      return onSelect(selection[0]);
    },
    [onSelect],
  );

  return (
    <QLookup.DataProvider.QuickSearchV2
      qriClient={qriApiClient}
      quickSearchClient={globalSearchServiceApiClient}
      resource={LOOKUP_RESOURCES}
      must={SEARCH_CLAUSE}
    >
      <QLookup.SingleSelect<ReusableContentRow>
        displayFilters={false}
        accessors={{ id: 'id' }}
        action="Select"
        isOpen={isOpen}
        onCancel={onClose}
        onSelect={handleSelect}
        searchPlaceholder="Search documents"
        size="4xl"
        view={view}
      >
        <QModalHeader>
          <QText>Add embedded content</QText>
        </QModalHeader>
      </QLookup.SingleSelect>
    </QLookup.DataProvider.QuickSearchV2>
  );
};
